import { AppContextProvider } from "@contexts";
import * as Sentry from "@sentry/react";
import Root from "@src/components/layout";
import clientRoutes from "@src/modules/client/routes";
import memberRoutes from "@src/modules/member/routes";
import { Suspense, useEffect } from "react";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "../i18n/i18n";
import ErrorComponent from "./components/errors";
import Loading from "./components/loading";
import { clientPathPrefix } from "./modules/client/path";
import { memberPathPrefix } from "./modules/member/path";

declare const Canny: any;

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: "",
    element: <Root />,
    errorElement: <ErrorComponent />,
    children: [
      {
        path: "",
        element: <Navigate to={memberPathPrefix} />,
      },
      {
        path: memberPathPrefix,
        children: memberRoutes,
      },
      {
        path: clientPathPrefix,
        children: clientRoutes,
      },
    ],
  },
]);

function App() {
  useEffect(() => {
    const handlePreloadError = (event: Event) => {
      event.preventDefault();
      window.location.reload();
    };
    window.addEventListener("vite:preloadError", handlePreloadError);

    const appID = import.meta.env.VITE_CANNY_APP_ID;
    if (typeof Canny === "function" && appID) {
      console.log("Initializing Canny changelog with appID:", appID);
      try {
        Canny("initChangelog", {
          appID,
          position: "top",
          align: "left",
          theme: "light", // options: light [default], dark, auto
        });
      } catch (error) {
        // Optionally log error to Sentry or console
        console.error("Canny initChangelog failed", error);
      }
    }

    return () => {
      window.removeEventListener("vite:preloadError", handlePreloadError);
      if (typeof Canny === "function") {
        try {
          Canny("closeChangelog");
        } catch (error) {
          console.error("Canny closeChangelog failed", error);
        }
      }
    };
  }, []);

  return (
    <AppContextProvider>
      <Suspense fallback={<Loading />}>
        <RouterProvider router={router} />
      </Suspense>
      <ToastContainer />
    </AppContextProvider>
  );
}

export default App;
